import {FormatIcu} from '@tolgee/format-icu'
import {BackendFetch, DevTools, FormatSimple, Tolgee} from '@tolgee/react'
import {useCurrentEnv} from '../../hooks/use-current-env.js'
import {getPersistedLanguage} from './persistence.js'

export type AvailableLanguage = keyof typeof LangToLocaleMap

export const LangToLocaleMap = {
  en: 'en-GB',
  de: 'de-DE',
  pl: 'pl-PL',
  ro: 'ro-RO',
}

export const availableLanguages = ['en', 'de', 'pl-PL', 'ro']
export const availableLocales = availableLanguages.map(item => {
  const [lang] = item.split('-')
  return LangToLocaleMap[lang as AvailableLanguage]
})

// out of our available languages, pick the one that matches best on order of user preference
const preferredLanguage = navigator.languages.find(lang => availableLanguages.includes(lang))

function parseLanguage(value: string) {
  if (!value || !availableLanguages.includes(value)) return 'en'
  return value as AvailableLanguage
}

function getInitialLanguage(): AvailableLanguage {
  const lang = getPersistedLanguage() || preferredLanguage
  if (lang) return parseLanguage(lang)
  return 'en'
}

export function getLocale(value: string) {
  const [lang] = parseLanguage(value).split('-') // in case of polish, where the language also includes the locale, we need to only return the locale, so the split is needed
  return LangToLocaleMap[lang as AvailableLanguage]
}

/**
 * Given a language, finds the appropiate supported language by the system. Needed in case of languages with a specific locale (e.g. finds `pl-PL` when being passed `PL`).
 * @param lang The desired language to find.
 * @returns The supported language, defaults to `en` in case no supported language for the given input.
 */
export function findAppropiateLanguage(lang: string | undefined | null) {
  if (!lang) return 'en'

  const searchableLang = lang.toLowerCase().trim()

  return (
    availableLanguages.find(l => {
      const lowercased = l.toLowerCase()
      return lowercased.includes(searchableLang) || searchableLang.includes(lowercased)
    }) || 'en'
  )
}

export const language = getInitialLanguage()
export const locale = getLocale(language)

const currentEnv = useCurrentEnv()
const enableDevTools = currentEnv !== 'production'

export const tolgee = (enableDevTools ? Tolgee().use(DevTools()) : Tolgee())
  .use(FormatSimple())
  .use(FormatIcu())
  .use(
    BackendFetch({
      getPath: ({language, namespace}) => {
        const path = `/${namespace || 'common'}/${language}.json`

        // bundle translations at build time (on-premise / air-gapped builds) — served from /tolgee
        if (import.meta.env.VITE_APP_BUNDLE_TRANSLATIONS === 'true') return `/tolgee${path}`

        // otherwise (dev and the cloud-hosted admin panel) load live from the Tolgee CDN
        const url = new URL(import.meta.env.VITE_APP_TOLGEE_CDN_URL)
        url.pathname += path
        return url.toString()
      },
      fallbackOnFail: true,
    })
  )
  .init({
    availableLanguages,
    defaultLanguage: 'en',
    fallbackLanguage: 'en',
    language,

    // add all available namespaces here. tolgee will preload all of them ahead of time
    ns: [
      'common',
      'locales',
      'auth',
      'users',
      'validation',
      'forms',
      'sections',
      'wards',
      'patient',
      'user-groups',
      'categories',
      'substances',
      'consumables',
      'trade-names',
      'medication-profiles',
      'stability-rules',
      'solutions',
      'supportive-medications',
      'dose-reduction',
      'activity',
      'cleanroom',
      'companies',
      'customers',
      'events',
      'new-organisation',
      'organisations',
      'patients',
      'pharmacies',
      'reports',
    ],
    fallbackNs: 'common',

    ...(import.meta.env.VITE_APP_TOLGEE_API_URL ? {apiUrl: import.meta.env.VITE_APP_TOLGEE_API_URL} : {}),
    ...(import.meta.env.VITE_APP_TOLGEE_API_KEY ? {apiKey: import.meta.env.VITE_APP_TOLGEE_API_KEY} : {}),
  })
